import { Timeline, Fullscreen, Close } from "@mui/icons-material";
import {
  Card,
  CardContent,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  Dialog,
  AppBar,
  Toolbar,
  Slide,
} from "@mui/material";
import React from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import HorizontalStack from "../../../../components/wrappers/HorizontalStack";
import { useTranslation } from "react-i18next";

const dummy_chart_data = {
  WBC: [
    { date: "2025-05-01", value: 9.2 },
    { date: "2025-06-01", value: 12.4 },
    { date: "2025-07-01", value: 14.8 },
    { date: "2025-08-01", value: 11.3 },
    { date: "2025-09-01", value: 8.7 },
    { date: "2025-10-01", value: 10.1 },
  ],
  RBC: [
    { date: "2025-05-01", value: 6.2 },
    { date: "2025-06-01", value: 6.8 },
    { date: "2025-07-01", value: 7.4 },
    { date: "2025-08-01", value: 6.9 },
    { date: "2025-09-01", value: 7.0 },
    { date: "2025-10-01", value: 7.3 },
  ],
  PLT: [
    { date: "2025-05-01", value: 320 },
    { date: "2025-06-01", value: 410 },
    { date: "2025-07-01", value: 360 },
    { date: "2025-08-01", value: 450 },
    { date: "2025-09-01", value: 390 },
    { date: "2025-10-01", value: 410 },
  ],
  EOS: [
    { date: "2025-05-01", value: 0.3 },
    { date: "2025-06-01", value: 0.6 },
    { date: "2025-07-01", value: 0.9 },
    { date: "2025-08-01", value: 0.5 },
    { date: "2025-09-01", value: 0.4 },
    { date: "2025-10-01", value: 0.6 },
  ],
};

const chart_sizes = {
  small: 350,
  medium: 550,
  large: 750,
};

const chart_values = {
  wbc: "WBC",
  rbc: "RBC",
  plt: "PLT",
  eos: "EOS",
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function MedicalChart() {
  const { t } = useTranslation();

  const [size, setSize] = React.useState(chart_sizes.small);
  const [value, setValue] = React.useState(chart_values.wbc);
  const [openFullscreen, setOpenFullscreen] = React.useState(false);

  const handleChangeSize = (e) => setSize(e.target.value);
  const handleChangeValue = (e) => setValue(e.target.value);

  const handleOpenFullscreen = () => setOpenFullscreen(true);
  const handleCloseFullscreen = () => setOpenFullscreen(false);

  const ChartView = ({ height = size }) => (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={dummy_chart_data[value]}>
        <CartesianGrid stroke="#eee" strokeDasharray="5 5" />
        <XAxis dataKey="date" />
        <YAxis />
        <Tooltip />
        <Legend />
        <Line
          type="monotone"
          dataKey="value"
          stroke="#1976d2"
          strokeWidth={3}
          dot={{ r: 4 }}
          activeDot={{ r: 6 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );

  return (
    <>
      <Card sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <HorizontalStack>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
              <Timeline color="primary" sx={{ fontSize: 32 }} />
              <Typography variant="h4" fontWeight={700}>
                {t("recentHealthTimeline")} - {t(value)}
              </Typography>
              <IconButton onClick={handleOpenFullscreen} color="primary" title={t("OpenFullscreen")}>
                <Fullscreen />
              </IconButton>
            </Stack>

            <HorizontalStack spacing={2}>
              <FormControl>
                <InputLabel id="chart-size-label">{t("chartSize")}</InputLabel>
                <Select
                  labelId="chart-size-label"
                  id="chart-size"
                  value={size}
                  label={t("chartSize")}
                  onChange={handleChangeSize}
                >
                  <MenuItem value={chart_sizes.small}>{t("small")}</MenuItem>
                  <MenuItem value={chart_sizes.medium}>{t("medium")}</MenuItem>
                  <MenuItem value={chart_sizes.large}>{t("large")}</MenuItem>
                </Select>
              </FormControl>

              <FormControl>
                <InputLabel id="chart-value-label">{t("chartValue")}</InputLabel>
                <Select
                  labelId="chart-value-label"
                  id="chart-value"
                  value={value}
                  label={t("chartValue")}
                  onChange={handleChangeValue}
                >
                  <MenuItem value={chart_values.wbc}>{t("WBC")}</MenuItem>
                  <MenuItem value={chart_values.eos}>{t("EOS")}</MenuItem>
                  <MenuItem value={chart_values.plt}>{t("PLT")}</MenuItem>
                  <MenuItem value={chart_values.rbc}>{t("RBC")}</MenuItem>
                </Select>
              </FormControl>
            </HorizontalStack>
          </HorizontalStack>

          <ChartView />
        </CardContent>
      </Card>

      {/* Fullscreen Chart */}
      <Dialog
        fullScreen
        open={openFullscreen}
        onClose={handleCloseFullscreen}
        TransitionComponent={Transition}
      >
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <Typography sx={{ flex: 1 }} variant="h6" component="div">
              {t(value)} {t("chart")}
            </Typography>
            <IconButton edge="end" color="inherit" onClick={handleCloseFullscreen} title={t("Close")}>
              <Close />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Stack sx={{ p: 4, height: "100%" }}>
          <ChartView height="90%" />
        </Stack>
      </Dialog>
    </>
  );
}
